import { DatePicker, InputNumber, Select, Space, Tag, Typography } from "antd";
import dayjs from "dayjs";
import { ControllerBindingRow } from "./ControllerBindingRow";
import { useControllers } from "../../Contexts/dashboardHooks";
import { boundController, controllerSingleDate } from "../../Processing/controllerBindings";

const { Text } = Typography;

/**
 * One calendar day, not a range. Body maps read a single day, which a start/end range
 * cannot express. When the section is dynamically controlled, it follows a date
 * controller and shows the END day of that controller's range.
 */
export interface SingleDateFormFields {
  /** UUID of the Slicer this date section follows, when it is bound. */
  singleDateControllerId?: string;
  dateMode: "relative" | "absolute";
  dateOffset: number;
  fixedDate: string;
}

export const SINGLE_DATE_DEFAULTS: SingleDateFormFields = {
  dateMode: "relative",
  dateOffset: 0,
  fixedDate: "",
};

interface SingleDateFormProps {
  value: SingleDateFormFields;
  onChange: (patch: Partial<SingleDateFormFields>) => void;
  description?: string;
  /** Show the "Dynamically controlled" row — the host widget's `dynamic.bindableSections`. */
  bindable?: boolean;
  /** Control size. The slicer widget passes "middle"; the Add Widget modal keeps "small". */
  size?: "small" | "middle";
  onPopupOpenChange?: (open: boolean) => void;
  onSettle?: () => void;
}

export function SingleDateForm({
  value,
  onChange,
  description = "Pick the day this data comes from.",
  bindable = false,
  size = "small",
  onPopupOpenChange,
  onSettle,
}: SingleDateFormProps) {
  const controllers = useControllers();
  const controller = bindable
    ? boundController(value.singleDateControllerId, "single_date", controllers)
    : undefined;
  const isBound = controller != null;
  const shown: SingleDateFormFields = isBound
    ? {
        ...value,
        ...controllerSingleDate(controller.controls, controller.spec.slicer_builder_misc),
      }
    : value;

  return (
    <div className="awm-step-body">
      {bindable && (
        <ControllerBindingRow
          kind="single_date"
          controllerId={value.singleDateControllerId}
          onChange={(singleDateControllerId) => onChange({ singleDateControllerId })}
        />
      )}

      <div className={`awm-form-stack${isBound ? " awm-bound-section" : ""}`}>
        {description && (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {description}
          </Text>
        )}

        <Space size={8} align="center" wrap>
          <Select
            size={size}
            value={shown.dateMode}
            onChange={(dateMode) => onChange({ dateMode })}
            onOpenChange={onPopupOpenChange}
            style={{ width: 110 }}
            options={[
              { value: "relative", label: "Relative" },
              { value: "absolute", label: "Absolute" },
            ]}
          />
          {shown.dateMode === "relative" ? (
            <>
              <Tag color="blue" style={{ margin: 0, fontSize: 11 }}>today</Tag>
              <InputNumber
                size={size}
                value={shown.dateOffset}
                onChange={(v) => onChange({ dateOffset: v ?? 0 })}
                onBlur={onSettle}
                min={-365}
                max={0}
                style={{ width: 72 }}
              />
              <Text type="secondary" style={{ fontSize: 11 }}>days</Text>
            </>
          ) : (
            <DatePicker
              size={size}
              value={shown.fixedDate ? dayjs(shown.fixedDate, "YYYY-MM-DD") : null}
              onChange={(_d, ds) => onChange({ fixedDate: typeof ds === "string" ? ds : "" })}
              onOpenChange={onPopupOpenChange}
            />
          )}
        </Space>
      </div>
    </div>
  );
}
